import { normalizeSavedPracticeProject } from './videoLibrary';
import {
  renameProject,
  saveUploadProject,
  saveYouTubeProject,
  updateProjectSegments
} from './projectService';
import {
  deleteGuestLibraryRecordIfUnchanged,
  getGuestLibraryRecord,
  listGuestLibraryRecords
} from './guestLibraryService';
import type { NormalizedLibraryProject } from '../types';

export interface GuestLibraryMigrationSuccess {
  localId: string;
  cloudProject: NormalizedLibraryProject;
}

export interface GuestLibraryMigrationResult {
  migrated: GuestLibraryMigrationSuccess[];
  failed: Array<{ localId: string; error: unknown }>;
}

/**
 * Copies browser-local guest items to the signed-in account. A local record is
 * deleted only after its cloud project and media have both been saved.
 */
export const migrateGuestLibraryToAccount = async (): Promise<GuestLibraryMigrationResult> => {
  const records = await listGuestLibraryRecords();
  const result: GuestLibraryMigrationResult = { migrated: [], failed: [] };

  for (const record of records) {
    try {
      let saved: Awaited<ReturnType<typeof saveUploadProject>>;

      if (record.source.type === 'youtube') {
        saved = await saveYouTubeProject(
          record.source,
          record.title,
          record.segments,
          record.settings
        );
      } else {
        if (!record.videoBlob) {
          throw new Error('The locally saved video data is missing.');
        }
        const file = new File(
          [record.videoBlob],
          record.fileName || `${record.title || 'croop-video'}.mp4`,
          { type: record.mimeType || record.videoBlob.type || 'video/mp4' }
        );
        saved = await saveUploadProject(
          file,
          record.title,
          record.segments,
          record.settings
        );
      }

      const cloudProject = normalizeSavedPracticeProject(saved.id, saved.project);
      let lastSyncedUpdatedAt = record.updatedAt;
      let localCopyRemoved = false;

      // A guest can keep editing while a large video uploads. Re-read the local
      // record before deletion and push any newer segments/settings first. The
      // compare-and-delete closes the remaining race window.
      for (let attempt = 0; attempt < 5 && !localCopyRemoved; attempt += 1) {
        const latest = await getGuestLibraryRecord(record.id);
        if (!latest) {
          localCopyRemoved = true;
          break;
        }
        if (latest.updatedAt !== lastSyncedUpdatedAt) {
          await updateProjectSegments(saved.id, latest.segments, latest.settings);
          if (latest.title !== record.title) {
            await renameProject(saved.id, latest.title);
          }
          lastSyncedUpdatedAt = latest.updatedAt;
        }
        localCopyRemoved = await deleteGuestLibraryRecordIfUnchanged(
          record.id,
          latest.updatedAt
        );
      }

      if (!localCopyRemoved) {
        throw new Error('This project changed while it was being backed up. Its local copy was kept so it can retry.');
      }
      result.migrated.push({ localId: record.id, cloudProject });
    } catch (error) {
      result.failed.push({ localId: record.id, error });
    }
  }

  return result;
};
